import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { SelectInput } from '../inputs';
import { apiGet, apiPost, apiDelete, showErrorToast } from '../../services/apiClient';
import { API_ENDPOINTS } from '../../config/apiEndpoints';
import { UserPlatformSubRole } from '../../models/user';
import { useTranslation } from 'react-i18next';
import { toast } from '../../utils/toast';
import TextInput from '../inputs/textInput';

export interface Employee {
  id: number;
  name: string;
  email: string;
  subRole: UserPlatformSubRole;
  role: number;
  defaultLanguage: number;
  createdAt: string;
}

interface TeamManagementProps {
  tenantId: number;
}

const TeamManagement: React.FC<TeamManagementProps> = ({ tenantId }) => {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const SUB_ROLE_OPTIONS = [
    { key: t('Director'), value: String(UserPlatformSubRole.DIRECTOR) },
    { key: t('Truck Manager'), value: String(UserPlatformSubRole.TRUCK_MANAGER) },
    { key: t('Truck Driver'), value: String(UserPlatformSubRole.TRUCK_DRIVER) },
    { key: t('Reviewer'), value: String(UserPlatformSubRole.REVIEWER) },
    { key: t('None'), value: String(UserPlatformSubRole.NONE) },
  ];

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const fetchEmployees = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = API_ENDPOINTS.tenant.employees(tenantId);
      const response = await apiGet(url, {offset: page * rowsPerPage, limit: Math.min(rowsPerPage, 300), search: debouncedSearch});
      const data = await response.json();
      
      setEmployees(data.result || []);
      setTotalCount(data.count || 0);
    } catch (err) {
      setError(t('Failed to load team members'));
      showErrorToast(err, t('Failed to load team members'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tenantId) {
      fetchEmployees();
    }
  }, [tenantId, page, rowsPerPage, debouncedSearch]);

  const handleRoleChange = async (userId: number, newRole: UserPlatformSubRole) => {
    try {
      const url = API_ENDPOINTS.tenant.updateEmployeeRole(tenantId, userId);
      
      await apiPost(url, { subRole: newRole });
      toast.success(t('Role updated successfully'));
      fetchEmployees();
    } catch (err) {
      showErrorToast(err, t('Failed to update role'));
    }
  };

  const handleRemoveEmployee = async (userId: number, userName: string) => {
    if (!window.confirm(t('Are you sure you want to remove {{name}} from the team?', { name: userName }))) {
      return;
    }

    try {
      const url = API_ENDPOINTS.tenant.removeEmployee(tenantId, userId);
      
      await apiDelete(url);
      toast.success(t('Employee removed successfully'));
      fetchEmployees();
    } catch (err) {
      showErrorToast(err, t('Failed to remove employee'));
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
          {t('Team Management')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('Manage your team members, their roles, and permissions.')}
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <TextInput
          fullWidth
          placeholder={t('Search by name or email')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('Name')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('Email')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('Role')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('Joined')}</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 100 }}>{t('Actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('No team members found')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  employees.map((employee) => (
                    <TableRow key={employee.id}>
                      <TableCell>{employee.name}</TableCell>
                      <TableCell>{employee.email}</TableCell>
                      <TableCell sx={{ minWidth: 200 }}>
                        <SelectInput
                          fullWidth
                          value={String(employee.subRole)}
                          onChange={(e) => handleRoleChange(employee.id, Number(e.target.value) as UserPlatformSubRole)}
                          options={SUB_ROLE_OPTIONS}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(employee.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={t('Remove employee')}>
                          <IconButton
                            onClick={() => handleRemoveEmployee(employee.id, employee.name)}
                            color="error"
                            size="small"
                            disableRipple
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </>
      )}
    </Box>
  );
};

export default TeamManagement;
